import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, FileText, MapPin, AlertTriangle, Phone, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import SeoLongform from "@/components/SeoLongform";
import GeoAnswerBlock from "@/components/GeoAnswerBlock";

const TITLE = "Driving a Rental Van into Mexico — Mexican Insurance Required";
const DESC =
  "Rent a 12 or 15 passenger van in the U.S. and drive it into Mexico. Mexican liability insurance is required for every day you're across the border — roughly $50 per day.";

export const Route = createFileRoute("/mexico-travel")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://sprinter-van-rentals.com/mexico-travel" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sprinter-van-rentals.com/mexico-travel" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Can I take a rental Sprinter van into Mexico?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Cross-border travel into Mexico is allowed with prior written approval on your rental agreement and proof of Mexican auto liability insurance for every day the van is in Mexico.",
              },
            },
            {
              "@type": "Question",
              name: "How much does Mexican insurance cost for a passenger van?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Mexican liability coverage for a 12 or 15 passenger van typically runs about $50 per day, and it must be purchased for every day of the trip inside Mexico — not just the days you drive.",
              },
            },
            {
              "@type": "Question",
              name: "Does my U.S. insurance work in Mexico?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "No. U.S. and Canadian auto policies and credit card coverage are not recognized by Mexican authorities. You must carry a policy issued by a licensed Mexican insurer.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: MexicoTravelPage,
});

const requirements = [
  {
    icon: ShieldCheck,
    title: "Mexican liability insurance — ~$50/day",
    body: "Required by Mexican law for every single day the van is south of the border, including days it's parked. Coverage must be issued by a licensed Mexican insurer and list the van by VIN.",
  },
  {
    icon: FileText,
    title: "Written cross-border authorization",
    body: "Tell us before you book. We add a Mexico travel permission letter to your rental agreement — without it, the van is not authorized to leave the United States.",
  },
  {
    icon: MapPin,
    title: "Temporary Vehicle Import Permit (TIP)",
    body: "Not needed for the free zone (Baja California and the 20–26 km border strip). Required for mainland travel beyond that zone, obtained through Banjercito at the border.",
  },
  {
    icon: AlertTriangle,
    title: "Passports for every passenger",
    body: "Everyone in the van needs a valid passport or passport card. Minors traveling without both parents may need a notarized consent letter.",
  },
];

function MexicoTravelPage() {
  return (
    <main className="bg-white">
      <Navbar />
      <div className="pt-16">
        <section className="bg-[#0F172A] text-white px-6 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#F97316] text-xs tracking-[0.3em] uppercase font-body mb-4">
              Cross-Border Rentals
            </p>
            <h1 className="font-display font-black text-4xl md:text-6xl uppercase leading-none tracking-tight">
              Taking a Rental Van into Mexico
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
              You can rent a 12 or 15 passenger Sprinter, Transit, or minivan in the United
              States and drive it into Mexico — as long as it's approved in advance and you
              carry Mexican auto insurance for every day of the trip.
            </p>
            <div className="mt-8 inline-flex flex-col sm:flex-row gap-3">
              <Link
                to="/book"
                className="inline-flex items-center justify-center bg-[#F97316] text-white font-display font-bold text-sm tracking-[0.2em] uppercase px-8 py-4 hover:brightness-110 transition"
              >
                Start a Booking
              </Link>
              <a
                href="tel:+19713282665"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-display font-bold text-sm tracking-[0.2em] uppercase px-8 py-4 hover:bg-white/10 transition"
              >
                <Phone size={16} /> (971) 328-2665
              </a>
            </div>
          </div>
        </section>

        <section className="px-6 py-16 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="border-l-4 border-[#F97316] bg-[#F97316]/5 p-6 md:p-8">
              <h2 className="font-display font-black text-2xl md:text-3xl text-[#0F172A] tracking-tight">
                Mexican insurance is mandatory — about $50 per day
              </h2>
              <p className="mt-4 text-[#0F172A]/85 text-base md:text-lg leading-relaxed">
                Mexico does not recognize U.S. auto liability policies, rental company
                coverage, or credit card benefits. Every renter crossing the border must buy
                a separate policy from a licensed Mexican insurer. For a 12 or 15 passenger
                van, that runs <strong>roughly $50 per day</strong>, and you must purchase it
                for <strong>every day the van is in Mexico</strong> — including the days it's
                simply sitting at your hotel. A seven-day trip is about $350 in Mexican
                coverage on top of your rental rate.
              </p>
              <p className="mt-4 text-[#0F172A]/85 text-base md:text-lg leading-relaxed">
                Driving in Mexico without it isn't just a rental violation — it's a legal
                one. After an accident, an uninsured driver can have the vehicle impounded
                and be detained until damages are settled.
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#0F172A] tracking-tight mb-8">
              What you need before you cross
            </h2>
            <div className="grid gap-5 md:grid-cols-2">
              {requirements.map((r) => (
                <div key={r.title} className="border border-[#0F172A]/10 p-6">
                  <r.icon className="text-[#F97316]" size={24} />
                  <h3 className="font-display font-black text-lg text-[#0F172A] mt-4 tracking-tight">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-[#0F172A]/80 leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#0F172A] tracking-tight mb-6">
              How the process works
            </h2>
            <ol className="space-y-4">
              {[
                "Tell us at booking that the trip includes Mexico, with your border crossing and dates.",
                "We confirm the van is eligible and add written cross-border authorization to your agreement.",
                "You purchase Mexican liability insurance for every day of the trip (~$50/day) and send us the policy.",
                "If you're traveling past the free zone, obtain a Temporary Vehicle Import Permit at the border.",
                "Pick up the van, cross with your paperwork in the glovebox, and enjoy the trip.",
              ].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <CheckCircle2 className="text-[#F97316] shrink-0 mt-1" size={20} />
                  <span className="text-[#0F172A]/85 text-base md:text-lg leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
            <p className="mt-8 text-[#0F172A]/70 text-sm leading-relaxed">
              Planning a run to Canada instead?{" "}
              <Link to="/canada-travel" className="text-[#F97316] font-semibold underline">
                Read our Canada cross-border requirements
              </Link>
              .
            </p>
          </div>
        </section>

        <SeoLongform
          title="Renting a passenger van in the U.S. for travel into Mexico"
          paragraphs={[
            "Cross-border van rentals are one of the most common special requests we handle. Families heading to Rocky Point, church groups running mission trips into Sonora and Baja, wedding parties driving down to Ensenada, and production crews shooting south of the border all need the same thing: a large passenger van that is legally authorized to leave the United States and properly insured once it arrives. We arrange all of that at the time of booking rather than at the counter, so you're not scrambling for paperwork on departure morning.",
            "The single biggest cost surprise for first-time cross-border renters is Mexican liability insurance. It is not optional, it is not covered by your U.S. policy, and it is not covered by a credit card's rental benefit. Mexican law only recognizes policies issued by insurers licensed in Mexico, and for a 12 or 15 passenger van the daily premium is generally in the neighborhood of $50 per day. That premium is charged per calendar day the vehicle is inside Mexico, not per day of driving, so budget for the entire length of the stay.",
            "Where you're going also matters. Baja California and the narrow border strip along the northern frontier make up the free zone, where a Temporary Vehicle Import Permit is not required. Travel onto the mainland past that zone requires a TIP obtained from Banjercito at the crossing, along with a refundable deposit and the vehicle's registration and authorization letter. Because a rental van is not registered in your name, the authorization letter we provide is what makes the permit possible — plan ahead so it's in hand before you reach the border.",
            "Practical advice from renters who make the drive regularly: cross during daylight, keep the printed insurance policy and authorization letter in the glovebox rather than on a phone, carry pesos for the toll roads, and stick to the cuota highways where possible. Give us a call and we'll walk through your route, confirm the right van size for your group and luggage, and get the cross-border paperwork started before you ever pick up the keys.",
          ]}
        />
      <GeoAnswerBlock />
      </div>
    </main>
  );
}
